import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  Button,
  DateInput,
  ListEmpty,
  PageHeader,
  SearchEmpty,
  DataTable,
} from "components";
import { IDataTableColumn } from "components/DataTable/interface";
import { colors } from "global/globalStyle";
import dayjs from "dayjs";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";
import { useAppSelector } from "store/rootReducer";

import { ReportsService } from "services/reportsService";

import { FORMS_RELATORIOS } from "utils/entities/permission";

import { Container, ContainerBody, ContainerList } from "./styles";
import { ContainerLeftMenu } from "../../Administration/styles";
import { ReportMenu } from "../ReportMenu";

export function LogError() {
  const { t } = useTranslation();
  const today = new Date().toISOString().split("T")[0];
  const [listOfLogs, setListOfLogs] = useState([]);
  const [initialDate, setInitialDate] = useState<any>(today);
  const [endDate, setEndDate] = useState<any>(today);
  const [loading, setLoading] = useState<boolean>(false);

  const columns: IDataTableColumn[] = [
    {
      id: "description",
      label: t("common.description"),
      width: "55%",
      align: "left",
      useEllipsis: true,
      ellipsisMaxWidth: "400px",
    },
    {
      id: "createIn",
      label: t("common.date"),
      width: "22%",
      align: "center",
      format: (value) => {
        if (!value) return "N/A";
        const date = dayjs(value);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div>{date.format("DD/MM/YYYY")}</div>
            <div style={{ fontSize: "11px", color: colors.neutral_gray_40 }}>
              {date.format("HH:mm:ss")}
            </div>
          </div>
        );
      },
    },
    {
      id: "version",
      label: t("reports.columns.version"),
      width: "23%",
      align: "center",
    },
  ];

  const { user } = useAppSelector((state) => state.user);

  const { addToast } = useToast();
  const dispatch = useDispatch();

  const inputRefNew = React.useRef<HTMLInputElement>(null);

  const { read } = usePermissions(FORMS_RELATORIOS.LOG_ERROR);

  useEffect(() => {
    if (inputRefNew.current) {
      inputRefNew.current.focus();
    }
  }, []);

  async function searchReportLogs() {
    if (!user?.customerId) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.needCustomerId"),
      });
      return;
    }
    if (!initialDate) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.selectStartDate"),
      });
      return;
    }
    if (!endDate) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.selectEndDate"),
      });
      return;
    }

    if (initialDate > endDate) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.invalidDateRange"),
      });
      return;
    }

    setLoading(true);
    dispatch(showLoading());
    const { status, data } = await ReportsService.getAllLogErros(
      user?.customerId,
      initialDate + " 00:00",
      endDate + " 23:59",
      null,
    );

    if (status && data && data.data) setListOfLogs(data.data);
    if (status && data && !data.data) setListOfLogs([]);
    setLoading(false);
    dispatch(hideLoading());
  }

  return (
    <Container>
      <ContainerLeftMenu>
        <ReportMenu />
      </ContainerLeftMenu>
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <PageHeader
          quantity={listOfLogs.length > 0 ? listOfLogs.length : 0}
          id="header"
          label={t("reports.errorLogs")}
          background="secondary"
          addClick={undefined}
          disabled={false}
        />
        <ContainerBody>
          {read ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  marginTop: "20px",
                }}
              >
                <div>
                  <DateInput
                    label={t("reports.startDate")}
                    required={false}
                    value={initialDate}
                    setValue={setInitialDate}
                    width={200}
                  />
                </div>
                <div style={{ marginLeft: 20 }}>
                  <DateInput
                    label={t("reports.endDate")}
                    required={false}
                    value={endDate}
                    setValue={setEndDate}
                    width={200}
                  />
                </div>

                <div style={{ marginLeft: 12, marginTop: 25 }}>
                  <Button onClick={() => searchReportLogs()}>
                    {t("reports.generateReport")}
                  </Button>
                </div>
              </div>
              <ContainerList>
                <DataTable
                  columns={columns}
                  rows={listOfLogs}
                  loading={loading}
                  emptyMessage={t("reports.logError.none")}
                  maxHeight="60vh"
                  stickyHeader
                  striped
                />
              </ContainerList>
            </>
          ) : (
            <SearchEmpty
              textMessage={t("common.noPermissionForProfile")}
              typeIcon={"avatar"}
              marginTop="30px"
            />
          )}
        </ContainerBody>
      </div>
    </Container>
  );
}
